import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import type {
  CockpitMetricBreakdownRow,
  CockpitMetricDetailsView,
  CockpitTurnCountRow,
} from '../model/cockpitViewModel';
import {
  formatCanonicalInteger,
  formatOptionalCockpitDateTime,
} from '../model/formatters';
import { useCockpitStyles } from './cockpitStyles';

const BreakdownTable = ({
  title,
  rows,
}: {
  title: string;
  rows: CockpitMetricBreakdownRow[];
}) => {
  const classes = useCockpitStyles();
  if (rows.length === 0) return null;
  return (
    <div className={classes.metricBreakdownTable}>
      <Typography component="h4" variant="subtitle2">
        {title}
      </Typography>
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow>
            <TableCell>Identificação</TableCell>
            <TableCell align="right">Usos</TableCell>
            <TableCell align="right">Tokens de entrada</TableCell>
            <TableCell align="right">Tokens de saída</TableCell>
            <TableCell align="right">Custo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.key}>
              <TableCell>{row.label}</TableCell>
              <TableCell align="right">
                {formatCanonicalInteger(row.usages)}
              </TableCell>
              <TableCell align="right">
                {formatCanonicalInteger(row.inputTokens)}
              </TableCell>
              <TableCell align="right">
                {formatCanonicalInteger(row.outputTokens)}
              </TableCell>
              <TableCell align="right">{row.costsLabel ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/**
 * "Turnos" table (US-012): counts of user/agent turns per phase or per skill,
 * with the first/last observed instant — never the turn's text (AC-2). Rows
 * with no attributed skill still show as "Sem skill" (US-007).
 */
const TurnsTable = ({
  title,
  rows,
}: {
  title: string;
  rows: CockpitTurnCountRow[];
}) => {
  const classes = useCockpitStyles();
  if (rows.length === 0) return null;
  return (
    <div className={classes.metricBreakdownTable}>
      <Typography component="h4" variant="subtitle2">
        {title}
      </Typography>
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow>
            <TableCell>Identificação</TableCell>
            <TableCell align="right">Turnos de usuário</TableCell>
            <TableCell align="right">Turnos de agente</TableCell>
            <TableCell align="right">Primeiro instante</TableCell>
            <TableCell align="right">Último instante</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.key}>
              <TableCell>{row.label}</TableCell>
              <TableCell align="right">
                {formatCanonicalInteger(row.user)}
              </TableCell>
              <TableCell align="right">
                {formatCanonicalInteger(row.agent)}
              </TableCell>
              <TableCell align="right">
                {formatOptionalCockpitDateTime(row.firstAt)}
              </TableCell>
              <TableCell align="right">
                {formatOptionalCockpitDateTime(row.lastAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const ParcelList = ({
  parcels,
}: {
  parcels: CockpitMetricDetailsView['parcels'];
}) => {
  const classes = useCockpitStyles();
  if (!parcels) return null;
  return (
    <dl
      className={classes.metricParcelList}
      aria-label="Parcelas dos tokens de entrada"
    >
      {parcels.map(parcel => (
        <div key={parcel.label} className={classes.metricParcelRow}>
          <dt>{parcel.label}</dt>
          <dd>{parcel.displayValue}</dd>
        </div>
      ))}
    </dl>
  );
};

const LegacyNotice = ({
  legacy,
}: {
  legacy: CockpitMetricDetailsView['legacy'];
}) => {
  const classes = useCockpitStyles();
  if (!legacy) return null;
  return (
    <Typography
      variant="body2"
      className={classes.metricLegacyNotice}
      role="note"
      aria-label="Medição antiga"
    >
      Medição antiga: {formatCanonicalInteger(legacy.usages)} uso(s) fora do
      total ({formatCanonicalInteger(legacy.inputTokens)} tokens de entrada,{' '}
      {formatCanonicalInteger(legacy.outputTokens)} de saída).
    </Typography>
  );
};

/** The decomposition of the token totals: parcels, breakdown tables, turns, legacy. */
export const TokenDetails = ({
  details,
}: {
  details: CockpitMetricDetailsView;
}) => (
  <>
    <ParcelList parcels={details.parcels} />
    <BreakdownTable title="Por modelo" rows={details.byModel} />
    <BreakdownTable title="Por fase" rows={details.byPhase} />
    <BreakdownTable title="Por skill" rows={details.bySkill} />
    <TurnsTable title="Turnos — Fase" rows={details.turnsByPhase} />
    <TurnsTable title="Turnos — Skill" rows={details.turnsBySkill} />
    <LegacyNotice legacy={details.legacy} />
  </>
);
